// KPI-28 — kataloğa dosyadan içe aktarma: saf UI yardımcıları (React yok, ayrı test edilir).
// Dosya kabulü, satır düzenleme (kod yeniden slug), sorun tespiti (commit kapısı — sunucu
// `validateCommitRows` ile aynı kurallar), süzme/sayfalama, kategori seçenekleri.

import { parseFormula, slugifyKpiCode } from "@mahir/shared";
import type { CatalogImportAction, CatalogImportRowDto } from "../api/kpi-catalog-import";
import { KPI_CATALOG_IMPORT_COPY } from "./kpi-catalog-import-copy";

export const KPI_CATALOG_IMPORT_MAX_BYTES = 40 * 1024 * 1024;
export const KPI_CATALOG_IMPORT_PAGE_SIZE = 25;

export function catalogImportRejectReason(file: { name: string; size: number }): string | null {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  if (ext !== "xlsx" && ext !== "docx" && ext !== "pdf")
    return KPI_CATALOG_IMPORT_COPY.upload.rejectType;
  if (file.size > KPI_CATALOG_IMPORT_MAX_BYTES) return KPI_CATALOG_IMPORT_COPY.upload.rejectSize;
  return null;
}

export type CatalogImportFilter =
  | "all"
  | "create"
  | "map_existing"
  | "skip"
  | "issues"
  | "category";

export type ConfidenceBand = "high" | "medium" | "low";

export function confidenceBand(c: number): ConfidenceBand {
  if (c >= 0.8) return "high";
  if (c >= 0.6) return "medium";
  return "low";
}

export interface CategoryOption {
  code: string;
  label: string;
  /** Bu dosyadaki kategori satırından geliyor (henüz katalogda yok). */
  fromFile: boolean;
}

/**
 * Üst kategori seçenekleri: mevcut katalog kategorileri + dosyadaki (skip olmayan) kategori
 * satırları. Satırın kendisi listeden düşer (kendi üstü olamaz).
 */
export function categoryOptions(
  rows: readonly CatalogImportRowDto[],
  existing: readonly { code: string; label: string }[],
  selfCode?: string,
): CategoryOption[] {
  const out: CategoryOption[] = existing.map((e) => ({
    code: e.code,
    label: e.label,
    fromFile: false,
  }));
  const seen = new Set(out.map((o) => o.code));
  for (const r of rows) {
    if (r.kind !== "category" || r.action === "skip" || seen.has(r.code)) continue;
    seen.add(r.code);
    out.push({ code: r.code, label: r.label || r.rowLabel, fromFile: true });
  }
  return out.filter((o) => o.code !== selfCode);
}

export type RowIssue =
  | "empty_label"
  | "duplicate_code"
  | "code_taken"
  | "bad_category"
  | "bad_breakdown_parent"
  | "bad_formula";

/** Kırılım satırı (grup ya da üye) — `categoryCode` üst göstergeyi/grubu taşır, kategori değil. */
export function isBreakdownImportRow(row: Pick<CatalogImportRowDto, "kind">): boolean {
  return row.kind === "breakdown_group" || row.kind === "breakdown_member";
}

/**
 * Kırılım satırının üst yolu (üst sütunu): grup → "Toplam"; üye → "Toplam › Cinsiyete göre".
 * Etiket dosyadaki satırdan, yoksa kod.
 */
export function breakdownParentPath(
  row: CatalogImportRowDto,
  rows: readonly CatalogImportRowDto[],
): string {
  const byCode = new Map(rows.map((r) => [r.code, r]));
  const parts: string[] = [];
  let code = row.categoryCode;
  let guard = 0;
  while (code && guard < 4) {
    const r = byCode.get(code);
    parts.unshift(r ? r.label || r.rowLabel : code);
    if (!r || !isBreakdownImportRow(r)) break;
    code = r.categoryCode;
    guard += 1;
  }
  return parts.join(" › ");
}

/** Satır bazlı sorunlar (commit kapısı). skip satırları sorunsuz sayılır. */
export function rowIssues(
  row: CatalogImportRowDto,
  rows: readonly CatalogImportRowDto[],
  existingCodes: ReadonlySet<string>,
  existingCategoryCodes: ReadonlySet<string>,
): RowIssue[] {
  if (row.action === "skip") return [];
  const issues: RowIssue[] = [];
  if (row.action === "create") {
    if (row.label.trim() === "") issues.push("empty_label");
    if (existingCodes.has(row.code)) issues.push("code_taken");
    if (rows.some((o) => o.rowId !== row.rowId && o.action === "create" && o.code === row.code)) {
      issues.push("duplicate_code");
    }
    if (row.kind === "metric" && row.isDerived) {
      if (!row.formula || !parseFormula(row.formula).ok) issues.push("bad_formula");
    }
  }
  if (isBreakdownImportRow(row)) {
    // grup → dosyadaki (atlanmayan) gösterge satırı ya da katalog kodu; üye → dosyadaki grup satırı
    const want = row.kind === "breakdown_group" ? "metric" : "breakdown_group";
    const parent = rows.find(
      (o) => o.rowId !== row.rowId && o.action !== "skip" && o.code === row.categoryCode,
    );
    const ok =
      row.categoryCode !== null &&
      (parent ? parent.kind === want : existingCodes.has(row.categoryCode));
    if (!ok) issues.push("bad_breakdown_parent");
  } else if (row.categoryCode !== null) {
    const inFile = rows.some(
      (o) =>
        o.rowId !== row.rowId &&
        o.kind === "category" &&
        o.action !== "skip" &&
        o.code === row.categoryCode,
    );
    const ok =
      existingCategoryCodes.has(row.categoryCode) || existingCodes.has(row.categoryCode) || inFile;
    if (!ok || row.categoryCode === row.code) issues.push("bad_category");
  }
  return issues;
}

export function issueLabel(issue: RowIssue): string {
  const c = KPI_CATALOG_IMPORT_COPY.review;
  switch (issue) {
    case "empty_label":
      return c.issueEmptyLabel;
    case "duplicate_code":
      return c.issueDuplicateCode;
    case "code_taken":
      return c.issueCodeTaken;
    case "bad_category":
      return c.issueBadCategory;
    case "bad_breakdown_parent":
      return c.issueBadBreakdownParent;
    case "bad_formula":
      return c.issueFormula;
  }
}

export interface RowPatch {
  action?: CatalogImportAction;
  code?: string;
  label?: string;
  unit?: string | null;
  categoryCode?: string | null;
  update?: boolean;
  isDerived?: boolean;
}

/**
 * Satır düzenleme: kod her zaman slug'a indirgenir (sunucu `slugifyKpiCode(code) === code`
 * ister); map_existing'e dönerken kod mevcut hedefin kodu olur, create'e dönerken etiketten
 * yeniden üretilir (mevcut kodla çakışmasın diye `existingCodes` ile). Türetilmiş kapatılınca
 * formül korunur ama yazılmaz.
 */
export function patchRow(
  rows: readonly CatalogImportRowDto[],
  rowId: string,
  patch: RowPatch,
  existingCodes: ReadonlySet<string>,
): CatalogImportRowDto[] {
  return rows.map((r) => {
    if (r.rowId !== rowId) return r;
    let next: CatalogImportRowDto = { ...r };
    if (patch.action !== undefined && patch.action !== r.action) {
      next.action = patch.action;
      if (patch.action === "map_existing" && r.existing) next.code = r.existing.code;
      if (patch.action === "create" && (r.existing || existingCodes.has(r.code))) {
        next.code = slugifyKpiCode(r.label || r.rowLabel, existingCodes);
      }
      if (patch.action !== "map_existing") next.update = false;
    }
    if (patch.label !== undefined) next.label = patch.label.slice(0, 200);
    if (patch.code !== undefined) next.code = slugifyKpiCode(patch.code);
    if (patch.unit !== undefined) {
      const u = patch.unit?.trim() ?? "";
      next.unit = u === "" ? null : u.slice(0, 32);
    }
    if (patch.categoryCode !== undefined) {
      next.categoryCode = patch.categoryCode === "" ? null : patch.categoryCode;
    }
    if (patch.update !== undefined) next.update = patch.update;
    if (patch.isDerived !== undefined) {
      next.isDerived = patch.isDerived;
      next.aggregation = patch.isDerived
        ? "recompute"
        : r.aggregation === "recompute"
          ? "sum"
          : r.aggregation;
    }
    if (next.kind === "category") next = { ...next, unit: null, isDerived: false, formula: null };
    return next;
  });
}

export interface ActionCounts {
  rows: number;
  categories: number;
  create: number;
  mapExisting: number;
  skip: number;
  issues: number;
}

export function countRows(
  rows: readonly CatalogImportRowDto[],
  issuesByRow: ReadonlyMap<string, readonly RowIssue[]>,
): ActionCounts {
  const out: ActionCounts = {
    rows: rows.length,
    categories: 0,
    create: 0,
    mapExisting: 0,
    skip: 0,
    issues: 0,
  };
  for (const r of rows) {
    if (r.kind === "category" && r.action !== "skip") out.categories += 1;
    if (r.action === "create") out.create += 1;
    else if (r.action === "map_existing") out.mapExisting += 1;
    else out.skip += 1;
    if ((issuesByRow.get(r.rowId)?.length ?? 0) > 0) out.issues += 1;
  }
  return out;
}

export function computeIssues(
  rows: readonly CatalogImportRowDto[],
  existingCodes: ReadonlySet<string>,
  existingCategoryCodes: ReadonlySet<string>,
): Map<string, RowIssue[]> {
  const out = new Map<string, RowIssue[]>();
  for (const r of rows) out.set(r.rowId, rowIssues(r, rows, existingCodes, existingCategoryCodes));
  return out;
}

function fold(s: string): string {
  return s.toLocaleLowerCase("tr-TR").replace(/ı/gu, "i");
}

export function filterRows(
  rows: readonly CatalogImportRowDto[],
  filter: CatalogImportFilter,
  search: string,
  issuesByRow: ReadonlyMap<string, readonly RowIssue[]>,
): CatalogImportRowDto[] {
  const q = fold(search.trim());
  return rows.filter((r) => {
    if (filter === "issues" && (issuesByRow.get(r.rowId)?.length ?? 0) === 0) return false;
    if (filter === "category" && r.kind !== "category") return false;
    if (
      (filter === "create" || filter === "map_existing" || filter === "skip") &&
      r.action !== filter
    )
      return false;
    if (q === "") return true;
    return [r.rowLabel, r.label, r.code, r.unit ?? "", r.categoryCode ?? ""].some((v) =>
      fold(v).includes(q),
    );
  });
}

export function paginate<T>(items: readonly T[], page: number, pageSize: number): T[] {
  const start = Math.max(0, page) * pageSize;
  return items.slice(start, start + pageSize);
}

/** Commit'e gidebilir mi: sorunsuz ve yazılacak en az bir satır (create ya da map_existing+update). */
export function hasWritableRows(rows: readonly CatalogImportRowDto[]): boolean {
  return rows.some(
    (r) => r.action === "create" || (r.action === "map_existing" && r.update === true),
  );
}
